import * as React from "react";
import { Alert, Button, Card, Divider, Modal, Popconfirm, Space, Tag, Tooltip } from "antd";
import { CardTitle } from "../../../../components/antd/card/CardTitle";
import { CardContent } from "../../../../components/antd/card/CardContent";
import { request } from "../../../../services/cpln";
import { FormItemDescription } from "../../../../components/antd/form/FormItemDescription";
import { CloseOutlined, EditOutlined, PlusCircleOutlined, SyncOutlined } from "@ant-design/icons";
import { SelectRolesModal } from "../selectRolesModal";
import { CardCollapsibleContent } from "../../../../components/antd/card/CardCollapsibleContent";
import { Table } from "../../../../newcomponents/table/table";
import { IdentityInput } from "../../../../schema/types/identity/input";
import { useAppDispatch, useAppSelector } from "../../../../store/hooks";
import { v4 as uuidv4 } from "uuid";
import { checkCanReach, setCloudaccountLink } from "../../../../store/slices/browserServiceSlice";
import { BrowserServiceModal } from "../browserServiceModal";
import { formSetValue, formTriggerValidation, useCpFormValue } from "../../../gvc/testNewForm/utils";
import { store } from "../../../../store";
import { cloneDeep } from "lodash";
import { CpSwitch } from "../../../gvc/testNewForm/CpSwitch";
import { CpLabel } from "../../../gvc/testNewForm/CpLabel";
import { CpDescription } from "../../../gvc/testNewForm/CpDescription";
import { CpErrorWarning } from "../../../gvc/testNewForm/CpErrorWarning";
import { CpFormItem } from "../../../gvc/testNewForm/CpFormItem";
import { CpRadioGroup } from "../../../gvc/testNewForm/CpRadioGroup";
import { CpInput } from "../../../gvc/testNewForm/CpInput";
import { CpArrayString } from "../../../gvc/testNewForm/CpArrayString";
import { useCpFormContext } from "../../../../components/form/useCpFormContext";
import { CpErrorWarningArrayIcon } from "../../../gvc/testNewForm/CpErrorWarningArrayIcon";
import { CpKindSelect } from "../../../gvc/testNewForm/temp/CpKindSelect";

interface BrowsedItem {
  _cpln: { ref: string; label: string };
}

export const IdentityCreateCloudaccessGCP: React.FC = () => {
  const { id } = useCpFormContext();

  const theme = useAppSelector((state) => state.theme.theme);

  const gcpLogoPath = `/resources/logos/gcpLogo${theme === "dark" ? "Dark" : ""}.svg`;

  const isEnabled = useCpFormValue(`gcp.isEnabled`);
  const method = useCpFormValue(`gcp.method`);
  const ca = useCpFormValue(`gcp.cloudAccount`);
  const sa = useCpFormValue(`gcp.serviceAccount`);
  const bindings = useCpFormValue(`gcp.bindings`);

  const [isFetchingServiceaccounts, setIsFetchingServiceAccounts] = React.useState(false);
  const [serviceAccounts, setServiceAccounts] = React.useState<BrowsedItem[]>([]);
  React.useEffect(() => {
    if (method === "serviceAccount") {
      fetchServiceAccounts();
    }
  }, [ca.link, method]);

  async function fetchServiceAccounts() {
    try {
      setIsFetchingServiceAccounts(true);
      const servicesRes = await request({ service: "browser", url: `/browse${ca.link}` });
      const iamLink = servicesRes.data.links.find((l) => l.label === "IAM")?.href;
      if (!iamLink) {
        throw new Error("Cannot reach IAM service.");
      }
      let allServiceAccounts: BrowsedItem[] = [];
      let nextLink: string | undefined = `${iamLink}/global/serviceaccount`;
      while (nextLink) {
        const res = await request({ service: "browser", url: nextLink });
        allServiceAccounts = allServiceAccounts.concat(res.data?.data || []);
        nextLink = res.data.links?.find((l: any) => l.rel === "next")?.href;
      }
      setServiceAccounts(allServiceAccounts);
      formSetValue(id, `gcp.serviceAccount.error`, "");
      setIsFetchingServiceAccounts(false);
    } catch (e) {
      setIsFetchingServiceAccounts(false);
      setServiceAccounts([]);
      formSetValue(id, `gcp.serviceAccount.error`, e.message);
    }
  }

  const unmatchedServiceAccount = React.useMemo(() => {
    if (!sa.value || serviceAccounts.length === 0) return false;
    return !serviceAccounts.some((s) => (s as any).email === sa.value);
  }, [sa.value, serviceAccounts]);

  const [isAddingBindingItem, setIsAddingBindingItem] = React.useState<number | boolean>(false);
  const onDoneBindingItemForm = () => {
    const form = store.getState().form.instances[id];
    const values: IdentityInput = form.values as any;

    const itemForm = cloneDeep(values.gcp.itemForm);
    const currentBindings = cloneDeep(values.gcp.bindings);

    if (typeof isAddingBindingItem !== "boolean") {
      currentBindings.splice(isAddingBindingItem, 1, {
        id: uuidv4(),
        resource: itemForm.resource,
        roles: itemForm.roles.map((r) => r.value),
      });
    } else {
      currentBindings.push({
        id: uuidv4(),
        resource: itemForm.resource,
        roles: itemForm.roles.map((r) => r.value),
      });
    }
    formSetValue(id, `gcp.bindings`, currentBindings);
    formSetValue(id, `gcp.itemForm`, { roles: [], resource: "", resourceHref: "" });
    formTriggerValidation(id);
    setIsAddingBindingItem(false);
  };

  const onRemoveBindingItem = (index: number) => {
    const form = store.getState().form.instances[id];
    const values: IdentityInput = form.values as any;

    const currentBindings = cloneDeep(values.gcp.bindings);
    currentBindings.splice(index, 1);
    formSetValue(id, `gcp.bindings`, currentBindings);
    formTriggerValidation(id);
  };

  return (
    <>
      <div className="cpln-form-section cpln-ant-card-container">
        <Card>
          <CardTitle>
            <div className={`flex items-center`}>
              <img
                aria-label="GCP Cloud Access"
                style={{ width: "auto", height: 30 }}
                src={gcpLogoPath}
                className="self-center"
              />
              <CpErrorWarningArrayIcon names={["gcp"]} />
            </div>
          </CardTitle>

          <CardContent>
            <CpFormItem>
              <div className="flex items-center gap-2">
                <CpLabel name={`gcp.isEnabled`}>Configure</CpLabel>
                <CpSwitch name={`gcp.isEnabled`} />
              </div>
              <CpErrorWarning name={`gcp.isEnabled`} />
            </CpFormItem>
            <CardCollapsibleContent collapsed={!isEnabled}>
              <CpFormItem>
                <CpLabel name={`gcp.cloudAccount`}>GCP Cloud Account</CpLabel>
                <CpDescription>GCP cloud account is used to authenticate with GCP Services.</CpDescription>
                <CpKindSelect
                  kind="cloudaccount"
                  name={`gcp.cloudAccount`}
                  queries={[{ property: "provider", value: "gcp" }]}
                  optionValue="name"
                  style={{ maxWidth: 600 }}
                />
                <CpErrorWarning name={`gcp.cloudAccount`} />
              </CpFormItem>
              <CpFormItem>
                <CpLabel name={`gcp.method`}></CpLabel>
                <CpRadioGroup
                  name={`gcp.method`}
                  options={[
                    { label: "Use an Existing GCP Service Account", value: "serviceAccount" },
                    { label: "Configure Service Account Bindings", value: "bindings" },
                  ]}
                />
                <CpErrorWarning name={`gcp.method`} />
              </CpFormItem>
              {method === "serviceAccount" ? (
                <>
                  <Divider orientation={"left"} orientationMargin={0}>
                    Continue with an Existing GCP Service Account
                  </Divider>
                  {sa.error ? (
                    <>
                      <CpFormItem>
                        <CpLabel name={`gcp.serviceAccount.value`}>Service Account</CpLabel>
                        <CpInput name={`gcp.serviceAccount.value`} />
                        <CpErrorWarning name={`gcp.serviceAccount.value`} />
                      </CpFormItem>
                      <Alert
                        className="mt-2"
                        type={"error"}
                        showIcon
                        message={`Selected Cloud Account Failed to List GCP Service Accounts`}
                        description={sa.error}
                      />
                    </>
                  ) : serviceAccounts.length < 1 ? (
                    <>
                      <CpFormItem>
                        <CpLabel name={`gcp.serviceAccount.value`}>Service Account</CpLabel>
                        <CpInput name={`gcp.serviceAccount.value`} />
                        <CpErrorWarning name={`gcp.serviceAccount.value`} />
                      </CpFormItem>
                      <Alert
                        className="mt-2"
                        type={"info"}
                        showIcon
                        message={`No GCP Service Accounts Found`}
                        description={sa.error}
                      />
                    </>
                  ) : (
                    <>
                      <FormItemDescription>{sa.value || "Select a Service Account"}</FormItemDescription>
                      {unmatchedServiceAccount && (
                        <Alert
                          className="mt-2 mb-4"
                          type="warning"
                          showIcon
                          message="Unrecognized Service Account"
                          description={
                            <>
                              The configured service account <code>{sa.value}</code> was not found in the GCP service
                              account list.
                            </>
                          }
                        />
                      )}
                      <CpFormItem>
                        <CpLabel name={`gcp.serviceAccount.filter`}>Filter Service Accounts</CpLabel>
                        <Space.Compact>
                          <CpInput name={`gcp.serviceAccount.filter`} style={{ width: 400 }} />
                          <Tooltip title={"Refetch Service Accounts"}>
                            <Button
                              icon={<SyncOutlined />}
                              disabled={isFetchingServiceaccounts}
                              loading={isFetchingServiceaccounts}
                              onClick={() => fetchServiceAccounts()}
                            />
                          </Tooltip>
                        </Space.Compact>
                      </CpFormItem>
                      <CpFormItem>
                        <CpLabel name={`gcp.serviceAccount.value`}>Service Accounts</CpLabel>
                        <CpRadioGroup
                          name={`gcp.serviceAccount.value`}
                          vertical
                          options={serviceAccounts
                            .filter(
                              (option) =>
                                (option as any).email === sa.value ||
                                option._cpln.label.toLowerCase().includes(sa.filter.toLowerCase()) ||
                                (option as any).email.toLowerCase().includes(sa.filter.toLowerCase()),
                            )
                            .map((option) => ({ label: (option as any).email, value: (option as any).email }))}
                        />
                        <CpErrorWarning name={`gcp.serviceAccount.value`} />
                      </CpFormItem>
                    </>
                  )}
                </>
              ) : null}
              {method === "bindings" ? (
                <>
                  <Divider orientation={"left"} orientationMargin={0}>
                    <div className="flex items-baseline">
                      <div>Bindings</div>
                    </div>
                  </Divider>
                  {bindings.length > 0 ? (
                    <Table<any>
                      // TODO prefix all of these with identity create
                      tableId="bindings"
                      data={bindings}
                      hideSettings
                      columns={[
                        { id: "resource", label: "Resource", enableResize: true, size: 200 },
                        {
                          id: "roles",
                          label: "Roles",
                          enableResize: true,
                          size: 200,
                          cell: ({ row }) => {
                            const roles = row.original.roles || [];
                            return (
                              <div className="flex flex-wrap gap-2">
                                {roles.map((role) => (
                                  <Tag key={role}>{role}</Tag>
                                ))}
                              </div>
                            );
                          },
                        },
                        {
                          id: "action",
                          label: "",
                          enableResize: false,
                          size: 120,
                          cell: ({ row }) => {
                            return (
                              <div className="w-full flex justify-end">
                                <Space.Compact>
                                  <Button
                                    htmlType={`button`}
                                    onClick={() => {
                                      const itemToEdit = bindings[row.index];
                                      if (itemToEdit) {
                                        formSetValue(id, `gcp.itemForm`, {
                                          roles: itemToEdit.roles.map((r) => ({ id: uuidv4(), value: r })),
                                          resource: itemToEdit.resource,
                                          resourceHref: "",
                                          // no scope href
                                        });
                                      }
                                      setIsAddingBindingItem(row.index);
                                    }}
                                    type={"text"}
                                    icon={<EditOutlined />}
                                  />
                                  <Popconfirm title={"Confirm"} onConfirm={() => onRemoveBindingItem(row.index)}>
                                    <Button htmlType={`button`} type={"text"} icon={<CloseOutlined />} danger />
                                  </Popconfirm>
                                </Space.Compact>
                              </div>
                            );
                          },
                        },
                      ]}
                    />
                  ) : null}
                  <Modal
                    open={isAddingBindingItem !== false}
                    title="Binding"
                    okText="Add"
                    cancelText="Cancel"
                    onCancel={() => {
                      setIsAddingBindingItem(false);
                      formSetValue(id, `gcp.itemForm`, { roles: [], resource: "", resourceHref: "" });
                    }}
                    onOk={onDoneBindingItemForm}
                    destroyOnHidden
                  >
                    <BindingItemFormContent isAddingBindingItem={isAddingBindingItem} />
                  </Modal>
                  <Button
                    htmlType={`button`}
                    className="mt-4"
                    type={"dashed"}
                    icon={<PlusCircleOutlined />}
                    onClick={() => setIsAddingBindingItem(true)}
                  >
                    Add Binding
                  </Button>
                </>
              ) : null}
            </CardCollapsibleContent>
          </CardContent>
        </Card>
      </div>
    </>
  );
};

interface Props {
  isAddingBindingItem: number | boolean;
}
const BindingItemFormContent: React.FC<Props> = ({ isAddingBindingItem }) => {
  const { id } = useCpFormContext();

  const [isSelectingResource, setIsSelectingResource] = React.useState(false);
  const [isSelectingRoles, setIsSelectingRoles] = React.useState(false);

  const dispatch = useAppDispatch();
  const [isCheckingReach, setIsCheckingReach] = React.useState(false);
  const reachError = useAppSelector((state) => state.browserService.reachError);
  const canReach = useAppSelector((state) => state.browserService.canReach);
  const cloudAccountLink = useCpFormValue(`gcp.cloudAccount.link`);

  React.useEffect(() => {
    init();
  }, []);

  async function init() {
    setIsCheckingReach(true);
    if (cloudAccountLink) {
      dispatch(setCloudaccountLink(cloudAccountLink));
      await dispatch(checkCanReach()).unwrap();
    }
    setIsCheckingReach(false);
  }

  const resourceHref = useCpFormValue(`gcp.itemForm.resourceHref`);
  const roles = useCpFormValue(`gcp.itemForm.roles`);

  return (
    <>
      <CpFormItem>
        <div className="mb-2">
          <CpLabel name={`gcp.itemForm.resource`}>
            <span>Resource</span>
            <Tooltip
              title={
                cloudAccountLink
                  ? canReach
                    ? "Browse Resources"
                    : "Selected Cloud Account cannot access cloud"
                  : "Select a Cloud Account in the previous screen to browse resources."
              }
            >
              <Button
                className="ml-2"
                size={`small`}
                onClick={() => setIsSelectingResource(true)}
                disabled={!cloudAccountLink || !canReach || isCheckingReach}
              >
                Browse
              </Button>
            </Tooltip>
          </CpLabel>
        </div>
        <CpInput name={`gcp.itemForm.resource`} />
        <CpErrorWarning name={`gcp.itemForm.resource`} />
      </CpFormItem>
      <CpLabel name="skip">
        <span>Roles</span>
        <Tooltip
          title={
            cloudAccountLink
              ? canReach
                ? "Browse Roles"
                : "Selected Cloud Account cannot access cloud"
              : "Select a Cloud Account in the previous screen to browse roles."
          }
        >
          <Button
            className="ml-2"
            size={`small`}
            onClick={() => setIsSelectingRoles(true)}
            disabled={!cloudAccountLink || !canReach || isCheckingReach}
          >
            Browse
          </Button>
        </Tooltip>
      </CpLabel>
      <div className="-mt-2">
        <CpArrayString name={`gcp.itemForm.roles`} />
      </div>
      <BrowserServiceModal
        open={isSelectingResource}
        title={"Select Resource"}
        forGcpResource={{ isActive: true, cloudaccountLink: cloudAccountLink }}
        onClose={(selections) => {
          if (selections.length > 0) {
            // TODO if user changes the value above, href is not cleaned
            formSetValue(id, `gcp.itemForm.resource`, selections[0]._cpln.ref);
            formSetValue(id, `gcp.itemForm.resourceHref`, selections[0]._cpln.href || "");
          }
          setIsSelectingResource(false);
        }}
        selectionType={"single"}
      />
      {isSelectingRoles ? (
        <SelectRolesModal
          onClose={(selections) => {
            formSetValue(
              id,
              `gcp.itemForm.roles`,
              selections.map((s) => ({ id: uuidv4(), value: s })),
            );
            setIsSelectingRoles(false);
          }}
          itemHref={resourceHref}
          currentSelections={[...roles.map((r) => r.value)]}
          isEditing={isAddingBindingItem !== true}
          type={"resource"}
        />
      ) : null}
    </>
  );
};
